import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * AdminBookMetaForm — the left-column title/author/description card in
 * the admin book editor. Per DESIGN.md Artboard 4 the form has an explicit
 * Save button (orange gradient primary). The "auto-saved N min ago" header
 * is rendered by the parent breadcrumb bar; this form just announces its
 * dirty state and surfaces the Save action.
 *
 * Controlled shape:
 *   - `book` prop owns the canonical state; the form hydrates its local
 *     input state on mount and re-syncs whenever `book` changes (e.g. when
 *     the cover uploader returns a refreshed payload).
 *   - On successful save we call `onBookChange(updated)` so the parent's
 *     state — and every other sidebar — sees the new metadata immediately.
 *
 * Empty string handling: server accepts `null` to clear `author` /
 * `description`. We translate `"" → null` before PATCHing so the admin
 * can explicitly blank out a field without needing a separate "clear"
 * control. Title follows a different rule (non-empty required).
 */
export interface AdminBookMetaFormProps {
  book: BookView;
  onBookChange: (book: BookView) => void;
  className?: string;
}

interface FormState {
  title: string;
  author: string;
  description: string;
  // Pages / chapters are stored as strings while the user edits so an
  // intermediate state ("" or "1") doesn't get coerced to NaN. The submit
  // handler parses + nullifies before posting.
  pages: string;
  chapters: string;
  amazonAffiliateUrl: string;
}

function toForm(book: BookView): FormState {
  return {
    title: book.title,
    author: book.author ?? '',
    description: book.description ?? '',
    pages: book.pages == null ? '' : String(book.pages),
    chapters: book.chapters == null ? '' : String(book.chapters),
    amazonAffiliateUrl: book.amazonAffiliateUrl ?? '',
  };
}

function isDirty(form: FormState, book: BookView): boolean {
  return (
    form.title !== book.title ||
    form.author !== (book.author ?? '') ||
    form.description !== (book.description ?? '') ||
    form.pages !== (book.pages == null ? '' : String(book.pages)) ||
    form.chapters !== (book.chapters == null ? '' : String(book.chapters)) ||
    form.amazonAffiliateUrl !== (book.amazonAffiliateUrl ?? '')
  );
}

/**
 * Soft URL check — Amazon URLs come with affiliate tags and tracking
 * fragments, so we don't run a strict `new URL(...)` parse. We just
 * make sure it looks like an http(s) URL and isn't pasted with leading
 * whitespace. The server accepts up to 2000 chars and stores the value
 * verbatim.
 */
function validateAffiliateUrl(raw: string): string | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^https?:\/\//i.test(trimmed)) return 'invalid';
  if (trimmed.length > 2000) return 'invalid';
  return trimmed;
}

/**
 * Parses a numeric form field into the `number | null` shape the API
 * expects. Empty string clears the column. Invalid input (non-digits,
 * out-of-range) returns `'invalid'` so the submit handler can surface a
 * targeted error instead of silently coercing to NaN.
 */
function parseCount(raw: string, max: number): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(n) || n < 1 || n > max) return 'invalid';
  return n;
}

export function AdminBookMetaForm({ book, onBookChange, className }: AdminBookMetaFormProps) {
  const [form, setForm] = useState<FormState>(() => toForm(book));
  const [submitting, setSubmitting] = useState(false);

  // Re-hydrate whenever the upstream book prop moves. This happens when
  // BookCoverUploader or a label toggle returns a refreshed BookView —
  // the form mustn't stomp on those mutations, but also shouldn't throw
  // away the user's unsaved meta edits. Heuristic: only re-sync fields
  // that weren't locally dirty.
  useEffect(() => {
    setForm((prev) => {
      const fresh = toForm(book);
      return {
        title: prev.title === '' || prev.title === fresh.title ? fresh.title : prev.title,
        author: prev.author === fresh.author ? fresh.author : prev.author,
        description: prev.description === fresh.description ? fresh.description : prev.description,
        pages: prev.pages === fresh.pages ? fresh.pages : prev.pages,
        chapters: prev.chapters === fresh.chapters ? fresh.chapters : prev.chapters,
        amazonAffiliateUrl:
          prev.amazonAffiliateUrl === fresh.amazonAffiliateUrl
            ? fresh.amazonAffiliateUrl
            : prev.amazonAffiliateUrl,
      };
    });
  }, [book]);

  const dirty = isDirty(form, book);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = form.title.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }
    // Per-field validation for the new numeric inputs. The server enforces
    // its own bounds (1..10000 for pages, 1..2000 for chapters) but we
    // mirror them here for instant feedback.
    const pagesParsed = parseCount(form.pages, 10000);
    if (pagesParsed === 'invalid') {
      toast.error('Pages must be a whole number between 1 and 10,000.');
      return;
    }
    const chaptersParsed = parseCount(form.chapters, 2000);
    if (chaptersParsed === 'invalid') {
      toast.error('Chapters must be a whole number between 1 and 2,000.');
      return;
    }
    const affiliateUrlParsed = validateAffiliateUrl(form.amazonAffiliateUrl);
    if (affiliateUrlParsed === 'invalid') {
      toast.error('Amazon URL must start with http:// or https://');
      return;
    }
    setSubmitting(true);
    try {
      const updated = await booksService.update(book.id, {
        title,
        author: form.author.trim() === '' ? null : form.author.trim(),
        description: form.description.trim() === '' ? null : form.description.trim(),
        pages: pagesParsed,
        chapters: chaptersParsed,
        amazonAffiliateUrl: affiliateUrlParsed,
      });
      onBookChange(updated);
      toast.success('Book details saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the book.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-bold text-or-slate-900">Book details</h2>
        {dirty ? (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-or-orange-600">
            Unsaved changes
          </span>
        ) : null}
      </div>

      <FormField
        label="Title"
        required
        value={form.title}
        onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
        maxLength={500}
        placeholder="e.g. The Velvet Tunnel"
      />

      <FormField
        label="Author"
        value={form.author}
        onChange={(e) => setForm((prev) => ({ ...prev, author: e.target.value }))}
        maxLength={500}
        placeholder="Leave blank if unknown"
        help="Leave empty to clear the author."
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`book-${book.id}-description`}
          className="or-field-label text-[11px] font-semibold uppercase tracking-wider text-or-slate-600"
        >
          Description
        </label>
        <textarea
          id={`book-${book.id}-description`}
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          rows={6}
          maxLength={20000}
          placeholder="Short synopsis shown in the book detail modal."
          className={cn(
            'rounded-xl border border-or-slate-200 bg-white px-3.5 py-2.5 font-serif text-[14px] leading-relaxed text-or-slate-900 placeholder:text-or-slate-400',
            'focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30',
          )}
        />
        <span className="text-right text-[11px] text-or-slate-400">
          {form.description.length.toLocaleString()} / 20,000
        </span>
      </div>

      {/* Reading-tracker metadata. Both fields are optional — leaving them
          blank hides the corresponding tracking option on the reader-facing
          wizard (see docs/READING_TRACKER_PLAN.md §5.5). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Pages"
          type="number"
          inputMode="numeric"
          min={1}
          max={10000}
          step={1}
          value={form.pages}
          onChange={(e) => setForm((prev) => ({ ...prev, pages: e.target.value }))}
          placeholder="e.g. 262"
          help="Used by the reading-tracker for the pages and minutes options."
        />
        <FormField
          label="Chapters"
          type="number"
          inputMode="numeric"
          min={1}
          max={2000}
          step={1}
          value={form.chapters}
          onChange={(e) => setForm((prev) => ({ ...prev, chapters: e.target.value }))}
          placeholder="e.g. 46"
          help="Used by the reading-tracker for the chapters option."
        />
      </div>

      {/* Amazon affiliate URL. Surfaced as a subtle "Buy on Amazon" link
          under the author on the book detail view; click goes through the
          parent-PIN gate before opening in a new tab. Leave blank to hide
          the link. The full URL — including the affiliate tag — is stored
          verbatim, so paste it tagged. */}
      <FormField
        label="Amazon affiliate URL"
        type="url"
        inputMode="url"
        value={form.amazonAffiliateUrl}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, amazonAffiliateUrl: e.target.value }))
        }
        maxLength={2000}
        placeholder="https://www.amazon.com/dp/…?tag=…"
        help="Leave blank to hide the buy link on this book."
      />

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={() => setForm(toForm(book))}
          disabled={!dirty || submitting}
        >
          Revert
        </Button>
        <Button type="submit" size="md" disabled={!dirty || submitting}>
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
